// API error handling

export class ApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode: number,
    public details?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export class ValidationError extends ApiError {
  constructor(message: string, public fields: Record<string, string>) {
    super(message, 'VALIDATION_ERROR', 400, fields)
    this.name = 'ValidationError'
  }
}

export class ConflictError extends ApiError {
  constructor(message: string, public conflicts: unknown[]) {
    super(message, 'CONFLICT_ERROR', 409, conflicts)
    this.name = 'ConflictError'
  }
}

export class NotFoundError extends ApiError {
  constructor(message: string = 'Resource not found') {
    super(message, 'NOT_FOUND', 404)
    this.name = 'NotFoundError'
  }
}

export class UnauthorizedError extends ApiError {
  constructor(message: string = 'Unauthorized') {
    super(message, 'UNAUTHORIZED', 401)
    this.name = 'UnauthorizedError'
  }
}

export class ForbiddenError extends ApiError {
  constructor(message: string = 'Forbidden') {
    super(message, 'FORBIDDEN', 403)
    this.name = 'ForbiddenError'
  }
}

// Error handler wrapper
export async function handleApiCall<T>(apiCall: () => Promise<T>): Promise<T> {
  try {
    return await apiCall()
  } catch (error: any) {
    // Supabase error codes
    if (error.code === 'PGRST116') {
      throw new NotFoundError('Record not found')
    }

    if (error.code === '23505') {
      throw new ConflictError('Duplicate record', [])
    }

    if (error.code === '23503') {
      throw new ValidationError('Invalid foreign key reference', {})
    }

    if (error.code === '42501') {
      throw new ForbiddenError('Insufficient permissions')
    }

    // Auth errors
    if (error.message?.includes('JWT') || error.message?.includes('token')) {
      throw new UnauthorizedError('Invalid or expired session')
    }

    // Generic error
    throw new ApiError(
      error.message || 'An unexpected error occurred',
      error.code || 'UNKNOWN_ERROR',
      500,
      error
    )
  }
}
